import { all, dropTask } from 'ember-concurrency';
import { array, hash } from '@ember/helper';
import { Button, Chip } from 'tio-ui/components/buttons';
import { courseBadgeState } from 'tio-employee/utils/tas/applicationStateMap';
import { Divider } from 'tio-ui/components/utilities';
import {
  getAllValuesForTasField,
  getSingleValueForTasField,
  setSingleValueForTasField,
} from 'tio-common/utils/tuition-assistance/fields';
import { fn } from '@ember/helper';
import Modal from 'tio-reimagined/components/modal';
import Footer from 'tio-reimagined/components/footer';
import { Input } from 'tio-ui/components/forms';
import Select from 'tio-reimagined/components/select';
import { inputsForFields } from 'tio-common/utils/data/inputs-for-fields';
import { LinkTo } from '@ember/routing';
import { not, or, and } from 'tio-ui/utilities';
import { on } from '@ember/modifier';
import { service } from '@ember/service';
import { t } from 'ember-intl';
import { tracked } from '@glimmer/tracking';
import { VStack, HStack, Section, Header } from 'tio-ui/components/layout';
import AcceptConditions from 'tio-employee/components/tuition-assistance/forms/accept-conditions';
import ActionableList from 'tio-ui/components/actionable-list';
import Alert from 'tio-employee/components/tas/applications/alert';
import TasFaqs from 'tio-common/components/tas/faqs';
import TioAlert from 'tio-common/components/tio/alert';
import ApplicationProcess from 'tio-employee/components/tuition-assistance/application/application-process';
import ClaimsFinancialsCalculationsReadonlyCard from 'tio-common/components/tuition-assistance/program-details/claims-financials/calculations-readonly-card';
import Component from '@glimmer/component';
import CourseCashTag from 'tio-common/components/tas/course-cash-tag';
import CourseSubmitted from 'tio-employee/components/tas/applications/course-submitted';
import DescriptionList from 'tio-ui/components/description-list';
import { divide } from 'tio-ui/math';
import NavTabs from 'tio-ui/components/nav-tabs';
import PaymentDetailsCard from 'tio-common/components/tas/payment-details-card';
import ProgramCustomReadonlyField from 'tio-common/components/tuition-assistance/program-custom-readonly-field';
import ProgramReadonlyField from 'tio-common/components/tuition-assistance/program-readonly-field';
import safeFormatDate from 'tio-common/helpers/safe-format-date';
import safeFormatNumber from 'tio-common/helpers/safe-format-number';
import ShowCourseDrawer from 'tio-employee/components/tas/show-course-drawer';
import Table from 'tio-ui/components/table';
import TasApplicationsShowController from 'tio-employee/controllers/authenticated/tas/applications/show';
import TASCourseModel from 'tio-common/models/tas-course';
import TioErrorMessages from 'tio-common/components/tio/error-messages';
import { PhChatCenteredText, PhTrash } from 'ember-phosphor-icons';
import PartnerMessagingDrawer from 'tio-employee/components/drawers/partner-messaging-drawer';
import type { TransitionLogSignature } from 'tio-common/types/tuition-assistance';
import type ConditionModel from 'tio-common/models/condition';
import type Owner from '@ember/owner';
import type RouterService from '@ember/routing/router-service';
import type SessionContextService from 'tio-employee/services/session-context';
// This is for types. Anything we have custom may show an error.
import type StoreService from 'ember-data/store';
import type TasApplicationModel from 'tio-common/models/tas-application';
import type TASProgramTemplateModel from 'tio-common/models/tas-program-template';
import type TuitionAssistanceService from 'tio-common/services/tuition-assistance';
import UploadAttachments from 'tio-common/components/tuition-assistance/forms/upload-attachments';
import type AgreementModel from 'tio-common/models/agreement';
import type FeaturesService from 'tio-common/services/features';
import { PhPencil, PhCheckCircle, PhCaretLeft, PhClipboardText } from 'ember-phosphor-icons';
import StripeCardDisplay from 'tio-employee/components/tas/course-cash/stripe-card-display';
import formatDateString from 'tio-common/helpers/format-date-string';
import ReceiptBanner from 'tio-employee/components/tas/course-cash/receipt-banner';
import ReceiptUploadModal from 'tio-employee/components/tas/course-cash/receipt-upload-modal';
import { mapAssistancesToTransactions } from 'tio-employee/utils/course-cash/transaction-adapter';
import type {
  CourseCashReceipt,
  CourseCashTransaction,
} from 'tio-common/types/course-cash-transaction';
import type DigitalAssetsService from 'tio-common/services/digital-assets';
import type { DigitalAssetDetailSignature } from 'tio-common/services/digital-assets';
import type TasAssetModel from 'tio-common/models/tas-asset';
import type AttachmentModel from 'tio-common/models/attachment';

interface RouteSignature {
  Args: {
    model: {
      application: TasApplicationModel;
      dynamicEligibility: boolean;
      programEligibility: [string, string][];
    };
    controller: TasApplicationsShowController;
  };
}

type LockEmployeeStatusBasedOnType =
  | 'COURSE_START_DATE'
  | 'COURSE_END_DATE'
  | 'FULFILLED_DATE'
  | 'ON_COURSES_SUBMISSION'
  | 'ON_SUBMISSION';

const programTypesToIncludeInstanceAssets = new Set(['TAS.ProgramType.2', 'TAS.ProgramType.3']);

export default class AuthenticatedTasApplicationsShowComponent extends Component<RouteSignature> {
  @tracked acceptedConditions: ConditionModel[] = [];
  @tracked submitError = null;
  @tracked hasSubmitted = false;
  @tracked isGradeModalOpen = false;
  @tracked courseToEdit: null | TASCourseModel = null;
  @tracked isCourseDrawerOpen = false;
  @tracked courseShowing: null | TASCourseModel = null;
  @tracked deleteApplicationOpen = false;
  @tracked gpaBasedAmount = 0;
  @tracked isPartnerMessagingDrawerOpen = false;
  @tracked courseCashTransactions: CourseCashTransaction[] = [];
  @tracked isReceiptModalOpen = false;
  @tracked selectedReceiptTransaction: CourseCashTransaction | null = null;

  @service declare store: StoreService;
  @service declare router: RouterService;
  @service declare sessionContext: SessionContextService;
  @service declare tuitionAssistance: TuitionAssistanceService;
  @service declare features: FeaturesService;
  @service declare digitalAssets: DigitalAssetsService;

  courseApprovalStates = [
    'TAS.ApplicationState.PENDING_COURSES_APPROVAL',
    'TAS.ApplicationState.COURSES_APPROVED',
    'TAS.ApplicationState.COURSES_NOT_APPROVED',
    'TAS.ApplicationState.COURSES_REJECTED',
  ];

  programTemplate: TASProgramTemplateModel;

  constructor(owner: Owner, args: RouteSignature['Args']) {
    super(owner, args);
    this.programTemplate = args.model.application.tasProgramInstance.tasProgramTemplate;

    if (this.showGPAField) {
      this.setGPA(
        getSingleValueForTasField('GRADE_POINT_AVERAGE', args.model.application.fields) || '0'
      );
    }

    // Load Course Cash transactions from real assistance data
    this.loadCourseCashTransactions();
  }

  loadCourseCashTransactions() {
    const assistances = this.args.model.application.tasAssistances ?? [];
    this.courseCashTransactions = mapAssistancesToTransactions(assistances);
  }

  get typeClassification() {
    return this.programTemplate.typeClassification;
  }

  get hasUnverifiedCourseCashTransactions() {
    return this.courseCashTransactions.some((t) => t.receiptStatus === 'needed');
  }

  get unverifiedTransactions() {
    return this.courseCashTransactions.filter((t) => t.receiptStatus === 'needed');
  }

  get allAttachments() {
    const { application } = this.args.model;
    const courseAssets = application.activeCourses.flatMap((c) => c.tasAssets ?? []);
    const appAssets = application.tasAssets;
    const programAssets = application.tasProgramInstance.tasAssets ?? [];

    return [
      ...courseAssets,
      ...appAssets,
      // only include programAssets for type 2/3 programs
      ...(programTypesToIncludeInstanceAssets.has(this.typeClassification) ? programAssets : []),
    ];
  }

  get attachmentsListDescription() {
    return (
      getAllValuesForTasField(
        'COMPLETION_DOCUMENTS_ATTACHMENT_FIELD',
        this.programTemplate.fields
      ).join(', ') || 'None Required'
    );
  }

  get coursesApprovalAttachments() {
    return (
      getAllValuesForTasField(
        'COURSE_APPROVAL_DOCUMENTS_ATTACHMENT_FIELD',
        this.programTemplate.fields
      ).join(', ') || 'None Required'
    );
  }

  get requireAttachmentUploads() {
    const typeClassificationsToCheck = [
      'TAS.ProgramType.3',
      'TAS.ProgramType.4',
      'TAS.ProgramType.4.3',
    ];
    if (typeClassificationsToCheck.includes(this.typeClassification)) {
      return this.programTemplate.requireAttachmentsForCourseApproval;
    }
    return false;
  }

  get conditionsForSubmit() {
    return this.programTemplate.applicationApproveEvidenceConditions || [];
  }

  get showSubmitForEvidenceReview() {
    return this.args.model.application.state === 'TAS.ApplicationState.ATTEND';
  }

  get setRequestedTotalAmount() {
    return Math.min(this.gpaBasedAmount, this.args.model.application.requestedTotal);
  }

  submitForEvidenceApproval = dropTask(async () => {
    this.submitError = null;
    this.hasSubmitted = true;
    try {
      await this.saveAgreementsForConditions.perform();
      await this.store
        .adapterFor('tas-application')
        // @ts-expect-error - Ember Data adapter method not typed on MinimumAdapterInterface
        .requestCourseEvidence(this.args.model.application);

      const tasType = this.typeClassification === 'TAS.ProgramType.3' ? 'three' : 'four';
      this.router.transitionTo('authenticated.tas.confirmation', {
        queryParams: {
          tasType: tasType,
          applicationId: this.args.model.application.id,
          evidence: 'true',
        },
      });
    } catch (error) {
      console.error('Failed to submit application:', error);
      this.submitError = error;
    }
  });

  saveAgreementsForConditions = dropTask(async () => {
    const user = this.sessionContext.user;
    const agreements = this.acceptedConditions.map((condition) => {
      return this.store.createRecord<AgreementModel>('agreement', {
        user: user,
        condition: condition,
      });
    });
    const promises = agreements.map((agreement) => {
      return agreement.save();
    });
    const saved = await all(promises);
    return saved;
  });

  get hasAgreedAllConditions() {
    if (!this.conditionsForSubmit.length) {
      return true;
    }
    return this.conditionsForSubmit.length === this.acceptedConditions.length;
  }

  get hasRequiredAttachments() {
    return this.args.model.application.hasRequiredEvidenceAttachments;
  }

  get canSubmitApplication() {
    if (this.args.model.application.ignorePolicies) {
      return true;
    }
    const { isCourseGradeRequired, isMissingCourseGrades, state } = this.args.model.application;

    const sharedConditions =
      this.isActiveParticipant &&
      this.hasAgreedAllConditions &&
      this.hasRequiredAttachments &&
      !this.inEligible &&
      state === 'TAS.ApplicationState.ATTEND' &&
      this.hasGpaIfRequired; // TAS.TODO: rework when updating validation

    return isCourseGradeRequired ? sharedConditions && !isMissingCourseGrades : sharedConditions;
  }

  get hasGpaIfRequired() {
    // checks if the GPA field is required
    if (!this.showGPAField) {
      return true;
    }

    const hasField = Boolean(
      getSingleValueForTasField('GRADE_POINT_AVERAGE', this.args.model.application.fields)
    );

    return hasField;
  }

  get expenseType() {
    const expenseType = getSingleValueForTasField('EXPENSE_TYPE', this.programTemplate.fields);

    return expenseType ? `${expenseType}` : `Education`;
  }

  get courseBadge() {
    return courseBadgeState(this.args.model.application.state);
  }

  get showInstructions() {
    const typeClassificationsToHide = [
      'TAS.ProgramType.3',
      'TAS.ProgramType.4',
      'TAS.ProgramType.4.3',
    ];
    return (
      typeClassificationsToHide.includes(this.typeClassification) &&
      this.args.model.application.state === 'TAS.ApplicationState.ATTEND'
    );
  }

  get grades() {
    const exclusiveGradeOptions = getAllValuesForTasField(
      'EXCLUSIVE_ACADEMIC_PERFORMANCE_OPTIONS',
      this.programTemplate.fields
    ) as string[];
    if (exclusiveGradeOptions.length > 0) {
      return exclusiveGradeOptions;
    }

    return inputsForFields['COURSE_GRADE'].options.map((option) => option.value);
  }

  get selectedGrade() {
    return getSingleValueForTasField('COURSE_GRADE', this.courseToEdit!.fields) as string;
  }

  get programCustomFields() {
    const customFields = this.args.model.application.tasProgramInstance.customFields;
    return customFields.filter((field: { province: string }) => field.province === 'PROGRAM');
  }

  get isTypeTwoOrThreeApplication() {
    return (
      this.typeClassification === 'TAS.ProgramType.3' ||
      this.typeClassification === 'TAS.ProgramType.2'
    );
  }

  get showAddResult() {
    return this.args.model.application.state === 'TAS.ApplicationState.ATTEND';
  }

  get showCompletedGrade() {
    const showGradeStates = [
      'TAS.ApplicationState.PENDING_EVIDENCE_APPROVAL',
      'TAS.ApplicationState.EVIDENCE_APPROVED',
      'TAS.ApplicationState.EVIDENCE_REJECTED',
      'TAS.ApplicationState.FULFILLED',
      'TAS.ApplicationState.ABANDONED',
    ];
    return showGradeStates.includes(this.args.model.application.state);
  }

  get applicationAwaitingApproval() {
    const awaitingStates = [
      'TAS.ApplicationState.PENDING_COURSES_APPROVAL',
      'TAS.ApplicationState.COURSES_APPROVED',
      'TAS.ApplicationState.COURSES_NOT_APPROVED',
      'TAS.ApplicationState.COURSES_REJECTED',
    ];
    return awaitingStates.includes(this.args.model.application.state);
  }

  get assetClassificationString() {
    return this.courseApprovalStates.includes(this.args.model.application.state)
      ? 'COURSE_APPROVAL_DOCUMENTS'
      : 'COMPLETION_DOCUMENTS';
  }

  get showDependentInfo() {
    const typeClassificationsToCheck = ['TAS.ProgramType.2', 'TAS.ProgramType.3'];
    return (
      this.programTemplate.isDependentProgram &&
      typeClassificationsToCheck.includes(this.typeClassification)
    );
  }

  get lockEmployeeStatusBasedOn() {
    return this.programTemplate.lockEmployeeStatusBasedOn as LockEmployeeStatusBasedOnType;
  }

  get lockStatus() {
    const lockType: Record<LockEmployeeStatusBasedOnType, string> = {
      COURSE_START_DATE: 'start',
      COURSE_END_DATE: 'end',
      FULFILLED_DATE: 'fulfilled',
      ON_COURSES_SUBMISSION: 'submission',
      ON_SUBMISSION: 'submission',
    };
    return this.lockEmployeeStatusBasedOn in lockType
      ? lockType[this.lockEmployeeStatusBasedOn]
      : '';
  }

  get startOrEndDate() {
    if (this.lockStatus === 'start') {
      return getSingleValueForTasField('COURSES_BEGIN_DATE', this.args.model.application.fields);
    }
    if (this.lockStatus === 'end') {
      return getSingleValueForTasField('COURSES_END_DATE', this.args.model.application.fields);
    }
    // For 'submission' lock status, use today's date
    return new Date().toLocaleDateString('en-CA');
  }

  calculateEligibility() {
    const { programEligibility } = this.args.model;
    if (!programEligibility.length) {
      return false;
    }

    const dateValue = this.startOrEndDate;
    // If we're checking start/end dates and they're missing, can't validate eligibility
    if (!dateValue || typeof dateValue !== 'string') {
      return false;
    }

    // Use today's date for 'submission' lock status, otherwise use the provided start or end date.
    const enteredDate = new Date(dateValue);

    return programEligibility.some(([start, end]) => {
      const startDate = new Date(start);
      const endDate = new Date(end);
      return enteredDate >= startDate && enteredDate <= endDate;
    });
  }

  get inEligible() {
    const { application, dynamicEligibility } = this.args.model;
    const { ignorePolicies, state } = application;

    if (ignorePolicies || !this.lockStatus) return false;

    const completedStates = [
      'TAS.ApplicationState.FULFILLED',
      'TAS.ApplicationState.ABANDONED',
      'TAS.ApplicationState.REJECTED_PER_POLICY',
    ];
    if (completedStates.includes(state)) return false;
    if (['fulfilled', 'submission'].includes(this.lockStatus)) return false;

    return !(dynamicEligibility && this.calculateEligibility());
  }

  get requestedChanges() {
    const findNotApprovedDetails = (transitions: TransitionLogSignature[]) => {
      // Filter transitions for "NOT_APPROVED" states
      const notApprovedStates = transitions.filter((transition: TransitionLogSignature) =>
        transition.to.includes('NOT_APPROVED')
      );

      // If there are no "NOT_APPROVED" states, return null or an empty result
      if (notApprovedStates.length === 0) {
        return null;
      }

      // Get the last occurrence based on the timestamp
      const lastNotApproved = notApprovedStates.reduce((latest, current) =>
        new Date(current.timestamp) > new Date(latest.timestamp) ? current : latest
      );

      // Extract the relevant details
      return {
        rejectedBy: lastNotApproved.reason?.by?.name || 'Unknown',
        note: lastNotApproved.reason?.note || 'No note provided',
      };
    };
    // spreeeaaad operator
    return { ...findNotApprovedDetails(this.args.model.application.transitionLog) };
  }

  get changesNeeded() {
    const lastTransition = this.args.model.application.transitionLog.slice(-1)[0];
    return lastTransition?.from.includes('NOT_APPROVED') || false;
  }

  get calculateTotalByGPA() {
    return this.programTemplate.calculateTotalByGPA;
  }

  get showGPAField() {
    return (
      this.calculateTotalByGPA &&
      (this.typeClassification === 'TAS.ProgramType.3' ||
        this.typeClassification === 'TAS.ProgramType.4.3')
    );
  }

  get requestedTotal() {
    if (this.showGPAField) {
      return this.setRequestedTotalAmount;
    } else {
      return this.args.model.application.requestedTotal;
    }
  }

  get isPaid() {
    return this.args.model.application.isPaid;
  }

  get courseCashUsed() {
    return this.args.model.application.amounts?.course_cash?.used > 0;
  }

  get isActiveParticipant() {
    return (
      this.args.model.application.tasProgramInstance.employee?.tasParticipant?.isActive || false
    );
  }

  get disableUploadAttachments() {
    return ['TAS.ApplicationState.DEFINE_COURSES', 'TAS.ApplicationState.ABANDONED'].includes(
      this.args.model.application.state
    );
  }

  get shouldShowPaymentDetailsCard() {
    return (
      this.args.model.application.shouldDisplayPaymentDetails &&
      this.args.model.application.paymentsHistory.length > 0
    );
  }

  setGPA = async (value: string) => {
    const gpa = Number(value);
    setSingleValueForTasField('GRADE_POINT_AVERAGE', gpa, this.args.model.application.fields);
    this.gpaBasedAmount = this.tuitionAssistance.setTotalBasedOnGPA(gpa);
    setSingleValueForTasField(
      'FINAL_APPROVED_TOTAL',
      this.setRequestedTotalAmount,
      this.args.model.application.fields
    );
    await this.args.model.application.save();
  };

  closeApplicationSubmittedModal = () => {
    this.args.controller.applicationSubmitted = false;
  };

  didUpdateAcceptedConditions = (conditions: ConditionModel[] = []) => {
    this.acceptedConditions = conditions;
  };

  openGradeModal = (course: TASCourseModel) => {
    this.isGradeModalOpen = true;
    this.courseToEdit = course;
  };

  closeGradeModal = () => {
    this.isGradeModalOpen = false;
    this.courseToEdit = null;
  };

  onSelectionChange = async (key: string | null) => {
    setSingleValueForTasField('COURSE_GRADE', key, this.courseToEdit!.fields);
    await this.courseToEdit!.save();
  };

  openCourseDrawer = (course: TASCourseModel) => {
    this.courseShowing = course;
    this.isCourseDrawerOpen = true;
  };

  closeCourseDrawer = () => {
    this.courseShowing = null;
    this.isCourseDrawerOpen = false;
  };

  toggleDeleteApplicationModal = () => {
    this.deleteApplicationOpen = !this.deleteApplicationOpen;
  };

  openPartnerMessagingDrawer = () => {
    this.isPartnerMessagingDrawerOpen = true;
  };

  closePartnerMessagingDrawer = () => {
    this.isPartnerMessagingDrawerOpen = false;
  };

  get showTrashIcon() {
    if (this.isPaid || this.courseCashUsed) {
      return false;
    }
    const statesNotAllowed = [
      'TAS.ApplicationState.FULFILLED',
      'TAS.ApplicationState.ABANDONED',
      'TAS.ApplicationState.COURSES_REJECTED',
      'TAS.ApplicationState.EVIDENCE_REJECTED',
      'TAS.ApplicationState.REJECTED_PER_POLICY',
    ];
    if (statesNotAllowed.includes(this.args.model.application.state)) {
      return false;
    }
    if (
      this.typeClassification === 'TAS.ProgramType.4.2' ||
      this.typeClassification === 'TAS.ProgramType.4.3'
    ) {
      return (
        this.args.model.application.tasProgramInstance.state === 'TAS.ProgramInstanceState.PRIMED'
      );
    }

    return true;
  }

  get showChatIcon() {
    const companySetting =
      this.args.model.application?.tasProgramInstance?.tasProgramTemplate?.company
        ?.companySetting ?? {};
    if (companySetting?.tuitionAssistance?.isPartnerMessagingEnabled) {
      return true;
    }
    return false;
  }
  // This is just the total number of messages for now.
  get numberOfUnreadMessages() {
    const partnerMessages = this.args.model.application.comments.filter((comment) =>
      comment.tags?.includes('Partner Messaging')
    );
    const messageCount = partnerMessages.length;
    return messageCount > 0 ? messageCount : '';
  }

  // Course Cash Receipt Upload Handlers
  handleUploadReceipt = (transaction: CourseCashTransaction) => {
    this.selectedReceiptTransaction = transaction;
    this.isReceiptModalOpen = true;
  };

  handleCloseReceiptModal = () => {
    this.isReceiptModalOpen = false;
    this.selectedReceiptTransaction = null;
  };

  handleReceiptUpload = async (
    files: DigitalAssetDetailSignature[],
    transaction: CourseCashTransaction
  ) => {
    if (!transaction.assistanceId) {
      throw new Error('No assistance ID found for transaction');
    }

    // Find the assistance record
    const assistance = this.args.model.application.tasAssistances?.find(
      (a) => a.id === transaction.assistanceId
    );

    if (!assistance) {
      throw new Error('Could not find assistance record');
    }

    // Upload each file and create asset + attachment with CARD_USAGE_PROOF classification
    for (const fileDetails of files) {
      // Create TasAsset from S3 upload details
      const asset = this.digitalAssets.createAssetFromS3Details(
        'tasAsset',
        fileDetails
      ) as TasAssetModel;
      asset.classification = 'CARD_USAGE_PROOF';

      const application = this.args.model.application;

      // Link asset to assistance - update both sides of many-to-many relationship
      asset.tasAssistances = asset.tasAssistances
        ? [...asset.tasAssistances, assistance]
        : [assistance];

      // Link asset to application - update both sides of many-to-many relationship
      asset.tasApplications = asset.tasApplications
        ? [...asset.tasApplications, application]
        : [application];
      application.tasAssets.push(asset);

      // Save the asset to create the join record; avoid saving the assistance
      // because tas-assistances has read-only attributes on update.
      await asset.save();
    }

    await this.reloadCourseCashAssistances();
  };

  handleRemoveReceipt = async (receipt: CourseCashReceipt, transaction: CourseCashTransaction) => {
    if (!receipt.attachmentId) {
      throw new Error('No attachment ID found for receipt');
    }

    let attachment = this.store.peekRecord(
      'attachment',
      receipt.attachmentId
    ) as AttachmentModel | null;

    if (!attachment) {
      attachment = await this.store.findRecord('attachment', receipt.attachmentId);
    }

    if (!attachment) {
      throw new Error('Could not find attachment for receipt');
    }

    await attachment.destroyRecord();

    await this.reloadCourseCashAssistances();

    const updatedTransaction = this.courseCashTransactions.find((t) => t.id === transaction.id);
    if (updatedTransaction) {
      this.selectedReceiptTransaction = updatedTransaction;
    }
  };

  reloadCourseCashAssistances = async () => {
    const assistanceIncludes = [
      'tas-assistances',
      'tas-assistances.attachments',
      'tas-assistances.tas-assets',
      'tas-assistances.tas-assets.attachments',
    ];
    await this.args.model.application.reload({
      include: assistanceIncludes.join(','),
    });

    this.loadCourseCashTransactions();
  };

  deleteApplication = dropTask(async () => {
    try {
      if (this.isPaid || this.courseCashUsed) {
        this.toggleDeleteApplicationModal();
        return;
      } else {
        if (['TAS.ProgramType.2', 'TAS.ProgramType.3'].includes(this.typeClassification)) {
          await Promise.all([
            // @ts-expect-error - Ember Data adapter method not typed on MinimumAdapterInterface
            this.store.adapterFor('tas-application').abandonCourse(this.args.model.application),
            this.store
              .adapterFor('tas-program-instance')
              // @ts-expect-error - Ember Data adapter method not typed on MinimumAdapterInterface
              .abandonProgram(this.args.model.application.tasProgramInstance),
          ]);
        }
        // @ts-expect-error - Ember Data adapter method not typed on MinimumAdapterInterface
        await this.store.adapterFor('tas-application').abandonCourse(this.args.model.application);
      }
    } catch (error) {
      console.error('Failed to delete application:', error);
    }
    // TAS.TODO: temporary routing until tas dashboard is complete
    this.router.transitionTo('authenticated.tas.dashboard');
  });

  <template>
    <VStack>
      <Header>
        <div>
          {{! TAS.TODO: temporary routing until tas dashboard is complete }}
          <LinkTo class="flex justify-left items-center gap-4" @route="authenticated.tas.dashboard">
            <PhCaretLeft @size="20" class="inline" />
            {{t "tas.default"}}
          </LinkTo>
        </div>
      </Header>
      <HStack>
        <VStack class="w-full!">
          <div class="flex items-center justify-between w-full mb-4">
            <NavTabs
              class="flex-1"
              @tabs={{array
                (hash
                  label=(t "tas.applications.default")
                  route="authenticated.tas.applications.show"
                  model=@model.application
                )
                (hash
                  label=(t "tas.history.default")
                  route="authenticated.tas.applications.history"
                  model=@model.application
                )
              }}
            />
            <div class="flex items-center gap-2">
              {{#if this.showChatIcon}}
                <Button
                  @appearance="minimal"
                  {{on "click" this.openPartnerMessagingDrawer}}
                  class="mb-2"
                  data-test-partner-messaging-button
                >
                  <PhChatCenteredText @size="32" />
                  {{this.numberOfUnreadMessages}}
                </Button>
              {{/if}}
              {{#if this.showTrashIcon}}
                <Button
                  @appearance="minimal"
                  @intent="danger"
                  {{on "click" this.toggleDeleteApplicationModal}}
                  class="mb-2 text-danger"
                >
                  <PhTrash @size="32" />
                </Button>
              {{/if}}
            </div>
          </div>
        </VStack>
      </HStack>
      <HStack>
        <VStack class="w-full lg:w-2/3 lg:flex-none md:w-1/2 md:flex-none">
          {{! Course Cash Receipt Upload Banner }}
          <ReceiptBanner
            @transactions={{this.courseCashTransactions}}
            @onUploadReceipt={{this.handleUploadReceipt}}
          />

          {{#if @model.application.pcsCards.length}}
            <StripeCardDisplay
              @application={{@model.application}}
              @transactions={{this.courseCashTransactions}}
              @onUploadReceipt={{this.handleUploadReceipt}}
            />
          {{/if}}

          <Section>
            <:header>
              {{String @model.application.displayNameOrSemesterCode}}
              <CourseCashTag
                @isDirectPayProgram={{this.programTemplate.isDirectPayProgram}}
                @size="sm"
                class="font-semibold"
              />
            </:header>
            <:body>
              {{#if this.inEligible}}
                <TioAlert @type="error" @allowDismiss={{false}} class="mb-4">
                  <:header>
                    <p class="font-bold" data-test-eligibility-error>
                      {{t "start_date_eligibility.errorHeader" startEndSubmission=this.lockStatus}}
                    </p>
                  </:header>
                  <:body>
                    <p class="text-sm">
                      {{t "start_date_eligibility.errorText"}}
                    </p>
                  </:body>
                </TioAlert>
              {{/if}}
              {{#if this.changesNeeded}}
                <TioAlert @type="warning" @allowDismiss={{false}} class="mb-4">
                  <:header>
                    <p class="font-bold" data-test-requested-changes>
                      {{t
                        "tas.application.requested_changes"
                        name=this.requestedChanges.rejectedBy
                      }}
                    </p>
                  </:header>
                  <:body>
                    <p class="text-sm">
                      {{this.requestedChanges.note}}
                    </p>
                  </:body>
                </TioAlert>
              {{/if}}

              {{#unless this.isActiveParticipant}}
                <TioAlert @type="warning" @allowDismiss={{false}} class="mb-4">
                  <:header>
                    <p class="font-bold">
                      {{t "tuition_assistance.inactive_participant_warnings.dashboard_title"}}
                    </p>
                  </:header>
                  <:body>
                    <p class="text-sm">
                      {{t "tuition_assistance.inactive_participant_warnings.dashboard_description"}}
                    </p>
                  </:body>
                </TioAlert>
              {{/unless}}
              <Alert @application={{@model.application}} />
              <h2 class="font-medium text-tio-gray-700 my-4">
                {{@model.application.tasProgramInstance.programName}}
              </h2>
              {{! APPLICANT TABLE }}
              <Table @isLoading={{false}}>
                <:thead as |options|>
                  <options.tr>
                    <options.th>
                      {{t "tas.applications.applicant"}}
                    </options.th>
                    <options.th>
                      {{t "tas.applications.amount"}}
                    </options.th>
                    <options.th>{{t "tas.applications.submission_date"}}</options.th>
                    <options.th>{{t "tas.applications.status"}}</options.th>
                  </options.tr>
                </:thead>
                <:tbody as |options|>
                  <options.tr>
                    <options.td>{{@model.application.applicant}}</options.td>
                    <options.td>
                      {{safeFormatNumber
                        (divide this.requestedTotal 100)
                        style="currency"
                        currency="USD"
                      }}
                    </options.td>
                    <options.td>
                      {{safeFormatDate @model.application.applicationSubmittedDate}}
                    </options.td>
                    <options.td>
                      {{#if @model.application.inRepayment}}
                        <Chip @intent="warning">
                          {{t "tuition_assistance.application_details.in_repayment"}}
                        </Chip>
                      {{else}}
                        <Chip @intent={{@model.application.badgeIntent}}>
                          {{@model.application.badgeLabel}}
                        </Chip>
                      {{/if}}
                    </options.td>
                  </options.tr>
                </:tbody>
              </Table>
            </:body>
          </Section>
          <Section>
            <:header>{{t "tas.applications.details"}}</:header>
            <:body>
              {{! APPLICATION DETAILS }}
              <DescriptionList class="w-2/3 md:w-full" as |List|>
                <List.Group>
                  <List.Term>{{t "tas.applications.application_name"}}</List.Term>
                  <List.Details>{{@model.application.applicationName}}</List.Details>
                </List.Group>
                {{#if this.showDependentInfo}}
                  <List.Group>
                    <List.Term>{{t "tas.applications.dependent"}}</List.Term>
                    <List.Details
                    >{{@model.application.tasProgramInstance.dependentName}}</List.Details>
                  </List.Group>
                {{/if}}
                <List.Group>
                  <List.Term>{{t "tas.applications.start_date"}}</List.Term>
                  <List.Details>
                    {{formatDateString @model.application.coursesBeginDate}}
                  </List.Details>
                </List.Group>
                <ProgramReadonlyField
                  @name="SEMESTER_CODE"
                  @programTemplate={{this.programTemplate}}
                  @fields={{@model.application.fields}}
                  as |field|
                >
                  <List.Group>
                    <List.Term>{{field.programLabel}}</List.Term>
                    {{! @glint-expect-error: field.value is null or record }}
                    <List.Details>{{field.value}}</List.Details>
                  </List.Group>
                </ProgramReadonlyField>
                {{#if this.isTypeTwoOrThreeApplication}}
                  <ProgramReadonlyField
                    @name="SCHOOL_INSTITUTION_NAME"
                    @programTemplate={{this.programTemplate}}
                    @fields={{@model.application.tasProgramInstance.fields}}
                    as |field|
                  >
                    <List.Group>
                      <List.Term>{{field.programLabel}}</List.Term>
                      {{! @glint-expect-error: field.value is null or record }}
                      <List.Details>{{field.value}}</List.Details>
                    </List.Group>
                  </ProgramReadonlyField>
                  <ProgramReadonlyField
                    @name="PROGRAM_MAJOR"
                    @programTemplate={{this.programTemplate}}
                    @fields={{@model.application.tasProgramInstance.fields}}
                    as |field|
                  >
                    <List.Group>
                      <List.Term>{{field.programLabel}}</List.Term>
                      {{! @glint-expect-error: field.value is null or record }}
                      <List.Details>{{field.value}}</List.Details>
                    </List.Group>
                  </ProgramReadonlyField>
                  <ProgramReadonlyField
                    @name="PROGRAM_SPECIALIZATION"
                    @programTemplate={{this.programTemplate}}
                    @fields={{@model.application.tasProgramInstance.fields}}
                    as |field|
                  >
                    <List.Group>
                      <List.Term>{{field.programLabel}}</List.Term>
                      {{! @glint-expect-error: field.value is null or record }}
                      <List.Details>{{field.value}}</List.Details>
                    </List.Group>
                  </ProgramReadonlyField>
                  <ProgramReadonlyField
                    @name="ESTIMATED_PROGRAM_BEGIN"
                    @programTemplate={{this.programTemplate}}
                    @fields={{@model.application.tasProgramInstance.fields}}
                    as |field|
                  >
                    <List.Group>
                      <List.Term>{{field.programLabel}}</List.Term>
                      <List.Details>{{formatDateString field.value}}</List.Details>
                    </List.Group>
                  </ProgramReadonlyField>
                  <ProgramReadonlyField
                    @name="ESTIMATED_PROGRAM_COMPLETION"
                    @programTemplate={{this.programTemplate}}
                    @fields={{@model.application.tasProgramInstance.fields}}
                    as |field|
                  >
                    <List.Group>
                      <List.Term>{{field.programLabel}}</List.Term>
                      <List.Details>{{formatDateString field.value}}</List.Details>
                    </List.Group>
                  </ProgramReadonlyField>
                  <ProgramReadonlyField
                    @name="ENROLLMENT_OBJECTIVE"
                    {{! @glint-expect-error: not sure }}
                    @programTemplate={{@model.application.programInstance.tasProgramTemplate}}
                    @fields={{@model.application.tasProgramInstance.fields}}
                    as |field|
                  >
                    <List.Group>
                      <List.Term>{{field.programLabel}}</List.Term>
                      {{! @glint-expect-error: field.value is null or record }}
                      <List.Details>{{field.value}}</List.Details>
                    </List.Group>
                  </ProgramReadonlyField>
                  {{#each this.programCustomFields as |field|}}
                    <ProgramCustomReadonlyField @customField={{field}} as |field|>
                      <List.Group>
                        {{! @glint-expect-error: field.value is null or record }}
                        <List.Term>{{field.programLabel}}</List.Term>
                        {{! @glint-expect-error: field.value is null or record }}
                        <List.Details>{{field.value}}</List.Details>
                      </List.Group>
                    </ProgramCustomReadonlyField>
                  {{/each}}
                {{/if}}
              </DescriptionList>
            </:body>
          </Section>
          {{! COURSE DETAILS - EXPENSES }}
          <Section>
            <:header>{{this.expenseType}}</:header>
            <:body>
              <ActionableList @striped={{true}} as |l|>
                {{#each @model.application.activeCourses as |course|}}
                  <l.Row class="w-full">
                    <l.Term
                      class="w-96 cursor-pointer text-ocean-600 underline"
                      {{on "click" (fn this.openCourseDrawer course)}}
                    >
                      {{course.courseName}}
                    </l.Term>
                    <l.Item
                      class="w-96 cursor-pointer"
                      {{on "click" (fn this.openCourseDrawer course)}}
                    >
                      {{#if @model.application.isCreditBased}}
                        {{course.courseCredit}}
                        {{t "common.credits"}}
                      {{else}}
                        {{safeFormatNumber
                          (divide course.courseTotal 100)
                          style="currency"
                          currency="USD"
                        }}
                      {{/if}}
                    </l.Item>
                    {{#if this.applicationAwaitingApproval}}
                      <l.Item
                        class="ml-0 mr-2 flex items-center font-semibold text-ocean-600 cursor-pointer md:ml-auto"
                      >
                        <Chip @intent={{this.courseBadge.intent}}>
                          {{this.courseBadge.label}}
                        </Chip>
                      </l.Item>
                    {{else if this.showAddResult}}
                      <l.Item
                        class="ml-0 mr-2 flex items-center font-semibold text-ocean-600 cursor-pointer md:ml-auto"
                        {{on "click" (fn this.openGradeModal course)}}
                      >
                        {{#if course.courseGrade}}
                          {{course.courseGrade}}
                          <PhPencil @size="24" class="ml-2 text-tio-gray-700" />
                        {{else}}
                          {{t "tas.benefits_summary.add_result_simple"}}
                        {{/if}}
                      </l.Item>
                    {{else if this.showCompletedGrade}}
                      <l.Item
                        class="ml-0 mr-2 flex items-center font-semibold text-ocean-600 md:ml-auto"
                      >
                        {{course.courseGrade}}
                      </l.Item>
                    {{/if}}
                  </l.Row>
                {{/each}}
              </ActionableList>
            </:body>
          </Section>
          {{#if
            (and this.programTemplate.scholarshipsAreVisible @model.application.scholarships.length)
          }}
            <Section>
              <:header>
                {{t "tas.new.scholarships.default"}}
              </:header>
              <:body>
                {{! SCHOLARSHIPS }}
                <ActionableList @striped={{true}} as |l|>
                  {{#each @model.application.scholarships as |scholarship|}}
                    <l.Row class="w-full">
                      <l.Term class="w-96">{{scholarship.scholarshipName}}</l.Term>
                      <l.Item class="w-96">
                        {{safeFormatNumber
                          (divide scholarship.scholarshipAmount 100)
                          style="currency"
                          currency="USD"
                        }}
                      </l.Item>
                    </l.Row>
                  {{/each}}
                </ActionableList>
              </:body>
            </Section>
          {{/if}}

          {{! ATTACHMENTS }}
          <Section>
            <:header>
              <div>
                {{t "common.attachments"}}
                {{#if this.requireAttachmentUploads}}
                  <span class="text-error-400 text-xl">*</span>
                {{/if}}
              </div>
            </:header>
            <:body>
              <UploadAttachments
                @programTemplate={{this.programTemplate}}
                @classification={{this.assetClassificationString}}
                @attachable={{@model.application}}
                @attachableType="tasApplication"
                @useDropzone={{true}}
                @hideInstructions={{not this.showInstructions}}
                @disabled={{this.disableUploadAttachments}}
                @attachmentListItems={{this.allAttachments}}
              />
              <div class="flex items-center w-full">
              </div>
            </:body>
          </Section>
        </VStack>
        {{! Evidence Submission and Payment Details }}
        <VStack class="lg:w-1/3! md:w-1/2!">
          {{! Submit For Evidence Review - ASIDE }}
          {{#if this.showSubmitForEvidenceReview}}
            <Section
              class="w-full"
              @headerClass="flex text-sm items-center gap-2 font-medium tracking-wide"
            >
              <:header>
                <PhClipboardText @size="32" />
                <p class="text-balance">{{t "tas.application.submit_evidence_for_review"}}</p>
              </:header>
              <:body>
                <section>
                  {{#if this.requireAttachmentUploads}}
                    <div>
                      <div class="flex flex-row justify-between items-center">
                        <p class="font-medium">{{t "tas.benefits_summary.attachments"}}
                          <span class="text-error-400 ml-1">
                            *
                          </span>
                        </p>
                        <div>
                          <UploadAttachments
                            @programTemplate={{this.programTemplate}}
                            @classification="COMPLETION_DOCUMENTS"
                            @attachable={{@model.application}}
                            @attachableType="tasApplication"
                            @useDropzone={{false}}
                            @iconOnlyUpload={{true}}
                            @hideInstructions={{true}}
                            @hideAttachments={{true}}
                            @disabled={{this.disableUploadAttachments}}
                          />
                        </div>
                      </div>

                      <div class="flex items-center gap-3">
                        {{#if @model.application.isMissingRequiredEvidence}}
                          <PhCheckCircle @size="24" class="text-tio-gray-200 shrink-0" />
                        {{else}}
                          <PhCheckCircle
                            @size="24"
                            @weight="fill"
                            class="text-violet-500 shrink-0"
                          />
                        {{/if}}
                        <p>{{this.attachmentsListDescription}}</p>
                      </div>
                    </div>
                    <Divider class="mt-4" />
                  {{/if}}

                  {{#if this.conditionsForSubmit.length}}
                    <AcceptConditions
                      @conditions={{this.conditionsForSubmit}}
                      @onChange={{this.didUpdateAcceptedConditions}}
                    />
                  {{/if}}
                </section>

                <div class="py-4 text-center">
                  <Button
                    @intent="primary"
                    disabled={{not this.canSubmitApplication}}
                    @isRunning={{this.submitForEvidenceApproval.isRunning}}
                    {{on "click" this.submitForEvidenceApproval.perform}}
                    class="my-4"
                  >
                    {{t "tas.benefits_summary.submit_evidence"}}
                  </Button>
                  <p class="text-sm font-light">
                    {{t "tas.benefits_summary.helper_text"}}
                  </p>
                  {{#if this.submitError}}
                    <TioErrorMessages
                      @showErrors={{this.hasSubmitted}}
                      @error={{this.submitError}}
                      class="text-sm"
                      @icon="warning"
                    />
                  {{/if}}
                </div>
              </:body>
            </Section>
          {{/if}}

          <PaymentDetailsCard
            @showDetails={{this.shouldShowPaymentDetailsCard}}
            @requestedTotal={{this.requestedTotal}}
            @payments={{@model.application.paymentsHistory}}
            @isCreditBased={{@model.application.isCreditBased}}
            @totalCredits={{@model.application.totalCredits}}
          />

          <ClaimsFinancialsCalculationsReadonlyCard
            class="grow md:max-w-xl"
            @claimsFinancialsFields={{@model.application.claimsFinancialsCalculatorInput}}
            @showDetails={{@model.application.tasProgramInstance.tasProgramTemplate.calculateTotalByClaimsFinancials}}
          />

          <ApplicationProcess
            @application={{@model.application}}
            @programTemplate={{this.programTemplate}}
          />
          <TasFaqs @template={{this.programTemplate}} />
        </VStack>
      </HStack>
    </VStack>

    <Modal
      @isOpen={{@controller.applicationSubmitted}}
      @onClose={{this.closeApplicationSubmittedModal}}
      class="min-h-64 max-w-screen-sm!"
    >
      <:header>
        <span class="text-midnight font-semibold">{{t
            "tuition_assistance.submitted.application_submitted"
          }}</span>
      </:header>
      <:body>
        <CourseSubmitted @application={{@model.application}} @courseApprovalRequired={{true}} />
        <div class="mb-4 flex justify-center">
          <Button
            {{on "click" this.closeApplicationSubmittedModal}}
            @appearance="outlined"
            class="w-48"
          >
            {{t "common.close"}}
          </Button>
        </div>
      </:body>
    </Modal>
    <Modal
      @isOpen={{this.isGradeModalOpen}}
      @onClose={{this.closeGradeModal}}
      class="max-w-screen-sm!"
    >
      <:header>
        <h1 class="text-midnight font-semibold">{{t "tas.benefits_summary.add_result"}}</h1>
      </:header>
      <:body>
        <div class="flex justify-between mb-6">
          <h2 class="text-lg font-semibold text-gray-700">
            {{String (getSingleValueForTasField "COURSE_NAME" this.courseToEdit.fields)}}
          </h2>
          <div class="w-72">
            <Select
              @items={{this.grades}}
              @selected={{this.selectedGrade}}
              @onChange={{this.onSelectionChange}}
            />
          </div>
        </div>
        {{#if this.showGPAField}}
          <div class="flex flex-wrap justify-between gap-y-4 items-center">
            <label
              for="grade-point-average"
              class="font-semibold block mb-1 text-balance text-gray-600"
            >
              {{t "tas.application.grade_point_average"}}
              <span class="text-error-400">*</span>
            </label>
            <Input
              size="lg"
              type="number"
              min="0"
              max="4.0"
              step="0.01"
              data-test-grade-point-average
              id="grade-point-average"
              @value={{String
                (or (getSingleValueForTasField "GRADE_POINT_AVERAGE" @model.application.fields) 0)
              }}
              class="bg-transparent! w-1/2!"
              @onChange={{this.setGPA}}
            />
          </div>
        {{/if}}
      </:body>
      <:footer>
        <Footer
          @isRunning={{false}}
          @onCancel={{this.closeGradeModal}}
          @onSubmit={{this.closeGradeModal}}
        />
      </:footer>
    </Modal>
    <Modal
      @isOpen={{this.deleteApplicationOpen}}
      @onClose={{this.toggleDeleteApplicationModal}}
      class="max-w-screen-sm!"
    >
      <:header>
        {{#unless this.isPaid}}
          {{t "tas.application.delete_modal.title"}}
        {{/unless}}
      </:header>
      <:body>
        {{#if this.isPaid}}
          <p class="font-bold text-red-800 mb-4">
            {{t "tas.application.delete_modal.attention"}}
          </p>

          <p class="text-red-700">
            {{t "tas.application.delete_modal.prepaid_warning"}}
          </p>
        {{else}}
          <p class="mb-4">{{t "tas.application.delete_modal.content"}}</p>
        {{/if}}
      </:body>
      <:footer>
        {{#if this.isPaid}}
          <Footer
            @onSubmit={{this.toggleDeleteApplicationModal}}
            @submitText={{t "common.close"}}
          />
        {{else}}
          <Footer
            @isRunning={{this.deleteApplication.isRunning}}
            @onCancel={{this.toggleDeleteApplicationModal}}
            @onSubmit={{this.deleteApplication.perform}}
            @submitText={{t "common.delete"}}
          />
        {{/if}}
      </:footer>
    </Modal>
    {{#if this.isCourseDrawerOpen}}
      <ShowCourseDrawer
        @isOpen={{this.isCourseDrawerOpen}}
        @onClose={{this.closeCourseDrawer}}
        @course={{this.courseShowing}}
        @programTemplate={{this.programTemplate}}
        {{! @glint-ignore: To reuse this for all courses course needs to be set to null on close }}
        @header={{this.courseShowing.courseName}}
      />
    {{/if}}
    {{#if this.isPartnerMessagingDrawerOpen}}
      <PartnerMessagingDrawer
        @isOpen={{this.isPartnerMessagingDrawerOpen}}
        @onClose={{this.closePartnerMessagingDrawer}}
        @application={{@model.application}}
      />
    {{/if}}
    {{! Course Cash Receipt Upload Modal }}
    <ReceiptUploadModal
      @isOpen={{this.isReceiptModalOpen}}
      @transaction={{this.selectedReceiptTransaction}}
      @onClose={{this.handleCloseReceiptModal}}
      @onUpload={{this.handleReceiptUpload}}
      @onRemoveReceipt={{this.handleRemoveReceipt}}
    />
  </template>
}
